/**
 * Service for GEX Beta - Portal backend API
 *
 * Connects to the option_stream/gex-beta Django app endpoints.
 * Provides data for the GEX Ray Beta widget.
 */

import { apiClient } from './api';
import { qfRestGet, qfSubscribe } from './qfClient';
import { normalizeOptionsTicker, underlyingPriceTicker } from '../utils/tickerDisplay';
import type { GexTimeseriesResponse, GexRayApiResponse, GexRayWsUpdate, GexMode } from '../types/gexSnapshot';

// Shape returned by QuantForge /v1/gex-levels.
type QfGexLevels = {
  symbol: string;
  exp_filter: string;
  calc_type: string;
  date: string;
  n: number;
  candles: { minute: string; open: number; high: number; low: number; close: number }[];
  series: {
    minute: string;
    net_gex: number;
    top: { strike: number; gex: number }[];
    bottom: { strike: number; gex: number }[];
  }[];
  profile: { minute: string; strikes: number[]; net_gex_by_strike: number[] } | null;
};

// QF "minute" strings may lack a timezone suffix; treat bare timestamps as UTC.
function toEpochMs(minute: string): number {
  const iso = /[zZ]|[+-]\d\d:?\d\d$/.test(minute) ? minute : `${minute}Z`;
  return new Date(iso).getTime();
}

export function mapQfGexToLegacy(qf: QfGexLevels, mode: GexMode): GexRayApiResponse {
  const wall = (pts: { strike: number }[], i: number) => pts[i]?.strike ?? null;
  return {
    ticker: qf.symbol,
    mode,
    bucket: qf.series.map((s) => toEpochMs(s.minute)),
    ohlc: {
      open: qf.candles.map((c) => c.open),
      high: qf.candles.map((c) => c.high),
      low: qf.candles.map((c) => c.low),
      close: qf.candles.map((c) => c.close),
    },
    walls: {
      call_wall_1: qf.series.map((s) => wall(s.top, 0)),
      call_wall_2: qf.series.map((s) => wall(s.top, 1)),
      put_wall_1: qf.series.map((s) => wall(s.bottom, 0)),
      put_wall_2: qf.series.map((s) => wall(s.bottom, 1)),
    },
    net_gex: qf.series.map((s) => s.net_gex),
    profile: qf.profile
      ? { strikes: qf.profile.strikes, net_gex: qf.profile.net_gex_by_strike }
      : { strikes: [], net_gex: [] },
  };
}

// Near-the-money band, ±5% of spot. MUST match the history path's band
// (web/app/routes/gex_levels.py _STRIKE_RANGE_PCT = 0.05) so the live profile
// and a history reload show the same strike set — otherwise strikes would flicker
// in live then vanish on refetch.
const STRIKE_RANGE_PCT = 0.05;

/**
 * One option contract inside a profile:{T} snapshot.
 *
 * `metrics` is declared open because the wire payload carries more than the
 * GEX reduction below consumes, and the extra keys vary by deployment. Open
 * interest is the case that matters: it is not documented as present, but if
 * the feed ever does carry it under any of the usual names the Overview's
 * contract panels will pick it up without a change here.
 */
export interface QfContract {
  exp: string;
  strike: string;
  type: 'C' | 'P';
  latest?: { gamma?: number };
  metrics?: {
    total_volume?: number;
    vol_net?: number;
    [key: string]: number | undefined;
  };
}

// QF profile:{T} Centrifugo snapshot wire shape (from greeks-platform).
export interface QfProfileSnap {
  ticker: string;
  as_of: string;
  session_day: string;
  spot: string | null;
  contracts: QfContract[];
}

function floorToMinuteMs(asOf: string): number {
  const t = new Date(asOf).getTime();
  return t - (t % 60000);
}

export function reduceProfileToGexUpdate(snap: QfProfileSnap, mode: GexMode): GexRayWsUpdate {
  const spot = snap.spot == null ? 0 : parseFloat(snap.spot);
  const nextExp = snap.contracts.map((c) => c.exp).filter((e) => e >= snap.session_day).sort()[0];
  const byStrike = new Map<number, number>();
  for (const c of snap.contracts) {
    if (c.exp !== nextExp) continue;
    const g = c.latest?.gamma;
    const vol = mode === 'enriched' ? c.metrics?.vol_net : c.metrics?.total_volume;
    if (g == null || vol == null || !spot) continue;
    const k = parseFloat(c.strike);
    if (Math.abs(k / spot - 1) > STRIKE_RANGE_PCT) continue;
    const gex = g * 100 * vol * spot * (c.type === 'C' ? 1 : -1);
    byStrike.set(k, (byStrike.get(k) ?? 0) + gex);
  }
  const strikes = [...byStrike.keys()].sort((a, b) => a - b);
  const gexArr = strikes.map((k) => byStrike.get(k)!);
  const net_gex = gexArr.reduce((s, v) => s + v, 0);
  const sorted = strikes.map((k) => ({ k, g: byStrike.get(k)! }));
  const calls = sorted.filter((x) => x.g > 0).sort((a, b) => b.g - a.g);
  const puts = sorted.filter((x) => x.g < 0).sort((a, b) => a.g - b.g);
  return {
    ticker: snap.ticker,
    mode,
    bucket: floorToMinuteMs(snap.as_of),
    ohlc: { open: null, high: null, low: null, close: spot || null },
    walls: {
      call_wall_1: calls[0]?.k ?? null,
      call_wall_2: calls[1]?.k ?? null,
      put_wall_1: puts[0]?.k ?? null,
      put_wall_2: puts[1]?.k ?? null,
    },
    net_gex,
    profile: { strikes, net_gex: gexArr },
  };
}

export function subscribeGexRayLive(
  ticker: string,
  mode: GexMode,
  onUpdate: (u: GexRayWsUpdate) => void,
): () => void {
  const root = normalizeOptionsTicker(ticker.toUpperCase());
  return qfSubscribe(`profile:${root}`, (data) => {
    onUpdate(reduceProfileToGexUpdate(data as QfProfileSnap, mode));
  });
}

/**
 * The same profile:{T} snapshot, handed over unreduced.
 *
 * GEX Ray only needs gamma × volume collapsed by strike; the contract panels
 * on the Overview need the individual contracts, so they subscribe to the raw
 * payload. qfSubscribe reference-counts per channel, so both consumers share
 * one subscription rather than opening a second.
 */
export function subscribeContractProfile(
  ticker: string,
  onSnapshot: (snap: QfProfileSnap) => void,
): () => void {
  const root = normalizeOptionsTicker(ticker.toUpperCase());
  return qfSubscribe(`profile:${root}`, (data) => onSnapshot(data as QfProfileSnap));
}

// Portal API wrapper response format
interface APIResponse<T> {
  data: T;
  meta?: Record<string, unknown>;
  status: string;
}

// Re-export GexMode from types for backwards compatibility
export type { GexMode } from '../types/gexSnapshot';

// Wall data (call/put walls at each timestamp)
export interface GexWallData {
  x: number[]; // timestamps in milliseconds
  y: number[]; // strike prices
}

// OHLC candle data
export interface GexCandleData {
  x: number[]; // timestamps in milliseconds
  open: (number | null)[];
  high: (number | null)[];
  low: (number | null)[];
  close: (number | null)[];
}

// Volume bar data (net GEX per minute)
export interface GexVolumeData {
  x: number[]; // timestamps in milliseconds
  y: number[]; // net GEX values
}

// Profile interval data (GEX by strike)
export interface GexProfileInterval {
  strike_data: Record<string, number>; // strike -> GEX value
  spot_price?: number | null;
}

// Profile data (current + historical intervals)
export interface GexProfileData {
  current: GexProfileInterval;
  '1min_ago': GexProfileInterval;
  '5min_ago': GexProfileInterval;
  '15min_ago': GexProfileInterval;
}

// Full chart data response from API
export interface GexBetaChartData {
  ticker: string;
  next_expiration_only: boolean;
  mode: GexMode;
  candles: GexCandleData;
  walls: {
    major_pos: GexWallData; // call walls (top positive GEX)
    major_neg: GexWallData; // put walls (top negative GEX)
    minor_pos: GexWallData; // secondary call walls
    minor_neg: GexWallData; // secondary put walls
  };
  volume: GexVolumeData; // net GEX per minute (bar chart)
  profile: GexProfileData; // GEX by strike at different intervals
}

export const gexBetaService = {
  /**
   * Get all chart data for a ticker
   *
   * @param ticker - Ticker symbol (e.g., 'SPX', 'SPXW', 'SPY')
   * @param nextExpirationOnly - Filter to next expiration only
   * @param mode - GEX calculation mode: 'naive' or 'enriched'
   * @returns Chart data for the GEX Ray Beta widget
   */
  async getChartData(
    ticker: string,
    nextExpirationOnly = false,
    mode: GexMode = 'naive'
  ): Promise<GexBetaChartData> {
    const params: Record<string, string> = {
      mode,
    };
    if (nextExpirationOnly) {
      params.next_expiration = 'true';
    }

    const response = await apiClient.get<APIResponse<GexBetaChartData>>(
      `/api/option-stream/gex-beta/chart-data/${ticker.toUpperCase()}/`,
      params
    );
    return response.data;
  },

  /**
   * Get timeseries data for real-time GEX charting
   *
   * Returns columnar data format for efficient transfer.
   * Used with WebSocket updates for real-time charts.
   *
   * @param ticker - Ticker symbol (e.g., 'SPX', 'SPXW', 'SPY')
   * @param lookbackMinutes - Number of minutes of history (default: 390 = full trading day)
   * @returns Timeseries data with walls, net GEX, and profile
   */
  async getTimeseries(ticker: string, lookbackMinutes = 390): Promise<GexTimeseriesResponse> {
    const response = await apiClient.get<APIResponse<GexTimeseriesResponse>>(
      `/api/option-stream/gex-timeseries/${ticker.toUpperCase()}/`,
      { lookback: String(lookbackMinutes) }
    );
    return response.data;
  },

  /**
   * Get GEX Ray data from the V3 API
   *
   * Uses latest gamma values for accurate GEX calculation.
   * Filters to next expiration only (0DTE for indices).
   *
   * @param ticker - Ticker symbol (e.g., 'SPX', 'SPY')
   * @param mode - GEX calculation mode: 'naive' or 'enriched'
   * @param date - Optional date in YYYY-MM-DD format for historical data
   * @returns Columnar data optimized for Plotly charts
   */
  async getGexRayData(ticker: string, mode: GexMode = 'naive', date?: string): Promise<GexRayApiResponse> {
    const params: Record<string, string> = {
      symbol: ticker.toUpperCase(),
      calc_type: mode === 'enriched' ? 'inferred' : 'naive',
      exp_filter: 'nextexp',
      n: '2',
    };
    if (date) {
      params.date = date;
    }
    const qf = await qfRestGet<QfGexLevels>('/v1/gex-levels', params);
    const legacy = mapQfGexToLegacy(qf, mode);

    // Price candles always come from /v1/ohlc (dense UW spot_bars / same path as
    // Net Sweeper), never from gex-levels embedded candles. GEX series minutes
    // can diverge from equity-minute density; zip-by-index was wrong for equities
    // and SPXW roots return empty gex-levels candles. Align by minute onto the
    // GEX series spine. priceSymbol maps options roots (SPXW→SPX) for indices.
    const priceSymbol = underlyingPriceTicker(params.symbol);
    try {
      const ohlcParams: Record<string, string> = { symbol: priceSymbol };
      if (date) ohlcParams.date = date;
      const px = await qfRestGet<{
        candles: { minute: string; open: number; high: number; low: number; close: number }[];
      }>('/v1/ohlc', ohlcParams);
      const byMinute = new Map(px.candles.map((c) => [toEpochMs(c.minute), c]));
      legacy.ohlc = {
        open: legacy.bucket.map((b) => byMinute.get(b)?.open ?? null),
        high: legacy.bucket.map((b) => byMinute.get(b)?.high ?? null),
        low: legacy.bucket.map((b) => byMinute.get(b)?.low ?? null),
        close: legacy.bucket.map((b) => byMinute.get(b)?.close ?? null),
      };
    } catch {
      // Leave gex-levels candles (if any) rather than fail the whole GEX render.
    }
    return legacy;
  },
};
